import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate } from "react-router-dom";

import Layout from "../../../Layout/Layout";
import SupplierConstants from "../../../common/SupplierConstants";
import ItemService from "../../../services/supplier_appCommonService";

const defaultInvitation = {
  companyName: "ABC Engineering Pvt. Ltd.",
  category: "Electrical Components",
  subCategory: "Switched Accessories",
  template: "Electrical Components Supplier Qualification",
  email: "procurement@abcorg.com",
  submissionDeadline: "",
  message: "",
};

const isSubmittedRow = (row) =>
  row?.status === "Submitted" || Boolean(row?.referenceNumber);

const mapRow = (row, fallbackEmail) => ({
  companyName: row.companyName || defaultInvitation.companyName,
  category: row.category || defaultInvitation.category,
  subCategory: row.subCategory || defaultInvitation.subCategory,
  template: row.template || defaultInvitation.template,
  email: row.email || fallbackEmail || defaultInvitation.email,
  submissionDeadline: row.submissionDeadline || "",
  message: row.message || "",
  userId: row.userId || row.invitationId || "",
  invitationId: row.invitationId || "",
  referenceNumber: row.referenceNumber || "",
  status: isSubmittedRow(row) ? "Submitted" : row.status || "Invited",
});

const dedupeRows = (rows) => {
  const map = new Map();
  rows.forEach((row) => {
    const email = row.email || row.invitation?.email || "";
    const template = row.template || row.invitation?.template || "";
    const key = `${email}|${template}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, row);
      return;
    }
    /* Prefer invitation-bearing / submitted richer row */
    const preferNew =
      (isSubmittedRow(row) && !isSubmittedRow(existing)) ||
      (Boolean(row.invitationId) && !Boolean(existing.invitationId)) ||
      (isSubmittedRow(row) && isSubmittedRow(existing) && row.invitationId);
    if (preferNew) {
      map.set(key, {
        ...existing,
        ...row,
        invitationId: existing.invitationId || row.invitationId || "",
        referenceNumber: existing.referenceNumber || row.referenceNumber || "",
        status:
          isSubmittedRow(row) || isSubmittedRow(existing)
            ? "Submitted"
            : row.status || existing.status || "Invited",
      });
    }
  });
  return Array.from(map.values());
};

const VendorMyAssessments = () => {
  const navigate = useNavigate();

  localStorage.setItem("menu", "Vendor");
  localStorage.setItem("submenu", "My Assessments");

  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState("active");

  useEffect(() => {
    const stored = localStorage.getItem("vendorInvitation");
    let storedParsed = null;
    if (stored) {
      try {
        storedParsed = JSON.parse(stored);
      } catch (e) {
        storedParsed = null;
      }
    }

    /* Load all invitations/submissions for logged-in supplier email */
    const loadInvitation = async () => {
      try {
        let userData = {};
        try {
          userData = JSON.parse(localStorage.getItem("userData") || "{}");
        } catch (e) {
          userData = {};
        }
        const userEmail =
          storedParsed?.email || userData?.data?.email || "";

        const payload = userEmail
          ? { email: userEmail }
          : {};
        await ItemService.getPreQualificationList(payload).then((item) => {
          if (item?.status === 200 && item?.data?.length) {
            const unique = dedupeRows(item.data);
            const mapped = unique.map((row) => mapRow(row, userEmail));
            setItems(mapped);

            /* Keep header on latest invitation (prefer still-open) */
            const headerRow =
              mapped.find((row) => !isSubmittedRow(row)) ||
              mapped[mapped.length - 1];
            if (headerRow) {
              localStorage.setItem(
                "vendorInvitation",
                JSON.stringify(headerRow)
              );
            }
          } else if (storedParsed) {
            setItems([mapRow(storedParsed, storedParsed.email)]);
          }
        });
      } catch (e) {
        if (storedParsed) {
          setItems([mapRow(storedParsed, storedParsed.email)]);
        }
      }
    };
    loadInvitation();
  }, []);

  const headerInvitation =
    items.find((row) => !isSubmittedRow(row)) ||
    items[0] ||
    defaultInvitation;

  const activeItems = items.filter((row) => !isSubmittedRow(row));
  const submittedItems = items.filter((row) => isSubmittedRow(row));
  const completedItems = [];

  const getDeadline = (row) => {
    if (row?.submissionDeadline) {
      return row.submissionDeadline;
    }
    return "30-Sep-2025";
  };

  const handleContinueAssessment = () => {
    navigate(SupplierConstants.VENDOR_PREQUALIFICATION_FORM);
  };

  const assessmentCard = (row, allowContinue) => {
    const submitted = isSubmittedRow(row);
    const showContinue = allowContinue && !submitted;
    const progress = submitted ? 100 : showContinue ? 40 : 0;
    return (
      <div className="accordion mb-4" id="assessmentAccordion">
        <div className="accordion-item">
          <h2 className="accordion-header">
            <Row className="accordion-button">
              <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                <span className="iconLogo">QA</span>
              </Col>
              <Col xs={7} md={7} lg={7}>
                <span className="headerTitle">
                  {row?.template || defaultInvitation.template}
                </span>
                <span className="subHeaderTitle">
                  {row?.category || defaultInvitation.category}
                </span>
              </Col>
              <Col xs={4} md={4} lg={4} className="text-end pe-3">
                <span
                  className={`badge ${
                    submitted ? "bg-success" : "bg-warning text-dark"
                  }`}
                >
                  {submitted ? "Submitted" : "In Progress"}
                </span>
              </Col>
            </Row>
          </h2>
          <div className="accordion-collapse collapse show visible">
            <div className="accordion-body">
              <Row className="pt-2 pb-2">
                <Col
                  xs={12}
                  md={6}
                  lg={6}
                  className="commTopButtonRightLeftPadding"
                >
                  <p className="mb-1">
                    <strong>Invited By:</strong>{" "}
                    {row?.companyName || defaultInvitation.companyName}
                  </p>
                  <p className="mb-1">
                    <strong>Category:</strong>{" "}
                    {row?.category || defaultInvitation.category}
                  </p>
                  <p className="mb-1">
                    <strong>Reference Code:</strong>{" "}
                    {row?.referenceNumber || "Not assigned"}
                  </p>
                </Col>
                <Col
                  xs={12}
                  md={6}
                  lg={6}
                  className="commTopButtonRightLeftPadding"
                >
                  <p className="mb-1">
                    <strong>Validity:</strong> 12 Months
                  </p>
                  <p className="mb-1">
                    <strong>Submission Deadline:</strong> {getDeadline(row)}
                  </p>
                  <p className="mb-1">
                    <strong>Status:</strong> {row?.status || "In Progress"}
                  </p>
                </Col>
              </Row>
              <Row className="pb-3">
                <Col
                  xs={12}
                  md={12}
                  lg={12}
                  className="commTopButtonRightLeftPadding"
                >
                  <p className="fw-medium mb-1">Completion Progress</p>
                  <div className="progress" style={{ height: "10px" }}>
                    <div
                      className="progress-bar bg-success"
                      role="progressbar"
                      style={{ width: `${progress}%` }}
                      aria-valuenow={progress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      {progress}%
                    </div>
                  </div>
                </Col>
              </Row>
              {showContinue && (
                <Row className="pb-3">
                  <Col xs={12} className="text-end pe-4">
                    <button
                      className="btn btn-primary"
                      onClick={handleContinueAssessment}
                    >
                      Continue Assessment{" "}
                      <i className="bi bi-arrow-right"></i>
                    </button>
                  </Col>
                </Row>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            <h2 className="mainTitle">My Assessments</h2>
            <p className="subText">
              Complete your pending pre-qualification assessments
            </p>
            <hr />

            {/* Assessment header details from invitation */}
            <div className="accordion mb-4" id="headerAccordion">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">HD</span>
                    </Col>
                    <Col xs={11} md={11} lg={11}>
                      <span className="headerTitle">
                        {headerInvitation?.template ||
                          defaultInvitation.template}
                      </span>
                      <span className="subHeaderTitle">
                        Supplier Pre-Qualification Details
                      </span>
                    </Col>
                  </Row>
                </h2>
                <div className="accordion-collapse collapse show visible">
                  <div className="accordion-body">
                    <Row className="pt-2 pb-2">
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <p className="mb-1">
                          <strong>Company:</strong>{" "}
                          {headerInvitation?.companyName ||
                            defaultInvitation.companyName}
                        </p>
                        <p className="mb-1">
                          <strong>Category:</strong>{" "}
                          {headerInvitation?.category ||
                            defaultInvitation.category}
                        </p>
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <p className="mb-1">
                          <strong>Sub Category:</strong>{" "}
                          {headerInvitation?.subCategory ||
                            defaultInvitation.subCategory}
                        </p>
                        <p className="mb-1">
                          <strong>Template:</strong>{" "}
                          {headerInvitation?.template ||
                            defaultInvitation.template}
                        </p>
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        lg={4}
                        className="commTopButtonRightLeftPadding"
                      >
                        <p className="mb-1">
                          <strong>Email:</strong>{" "}
                          {headerInvitation?.email ||
                            defaultInvitation.email}
                        </p>
                        <p className="mb-1">
                          <strong>Deadline:</strong>{" "}
                          {getDeadline(headerInvitation)}
                        </p>
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs: Active / Submitted / Completed */}
            <ul className="nav nav-tabs" id="assessmentTab" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${activeTab === "active" ? "active" : ""}`}
                  type="button"
                  onClick={() => setActiveTab("active")}
                >
                  Active ({activeItems.length})
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${
                    activeTab === "submitted" ? "active" : ""
                  }`}
                  type="button"
                  onClick={() => setActiveTab("submitted")}
                >
                  Submitted ({submittedItems.length})
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link ${
                    activeTab === "completed" ? "active" : ""
                  }`}
                  type="button"
                  onClick={() => setActiveTab("completed")}
                >
                  Completed ({completedItems.length})
                </button>
              </li>
            </ul>

            <div className="tab-content pt-3" id="assessmentTabContent">
              {activeTab === "active" &&
                (activeItems.length ? (
                  activeItems.map((row, idx) => (
                    <React.Fragment key={row.invitationId || row.email || idx}>
                      {assessmentCard(row, true)}
                    </React.Fragment>
                  ))
                ) : (
                  <p className="text-muted p-3">No active assessments.</p>
                ))}
              {activeTab === "submitted" &&
                (submittedItems.length ? (
                  submittedItems.map((row, idx) => (
                    <React.Fragment
                      key={
                        row.referenceNumber ||
                        row.invitationId ||
                        row.email ||
                        idx
                      }
                    >
                      {assessmentCard(row, false)}
                    </React.Fragment>
                  ))
                ) : (
                  <p className="text-muted p-3">No submitted assessments.</p>
                ))}
              {activeTab === "completed" && (
                <p className="text-muted p-3">No completed assessments.</p>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </main>
  );
};

export default VendorMyAssessments;
